import { Modal } from '@lume/ui-components';
import { useState } from 'react';

import type { ActiveConflictAlert } from '../hooks/use-conflict-alerts';

/**
 * ConflictAlertDetailsModal — r7b §32 ADR-117.
 *
 * Modal de detalhes de ConflictAlert — §32.4 (tabela de bloqueios por
 * severity) + §32.5 (ações do operador: aceitar com justificativa / marcar
 * como falso positivo).
 *
 * Categorias canônicas ADR-117:
 *   - `cross-tenant-customer`  → Cliente cross-tenant
 *   - `conflict-of-interest`   → Conflito de interesse
 *   - `prior-case-same-party`  → Caso anterior com mesma parte
 *   - `data-confidentiality`   → Quebra potencial de sigilo
 *   - `regulatory-conflict`    → Conflito regulatório
 *
 * Severities: `low` · `medium` · `high` · `critical`
 *
 * Bloqueios automáticos por severity (ADR-117 §32.3):
 *   - `high`     → bloqueia H_c + MCP tools parcial
 *   - `critical` → bloqueia H_c + MCP tools + Composer (somente leitura)
 *
 * Consume `Modal` do DS v2.2.0 (`@lume/ui-components`).
 */
export function ConflictAlertDetailsModal({
  open,
  alert,
  onClose,
  onAcceptWithJustification,
  onMarkFalsePositive,
}: {
  open: boolean;
  alert: ActiveConflictAlert | null;
  onClose: () => void;
  onAcceptWithJustification?: (alertId: string, justification: string) => void;
  /** Apenas gestor/admin_tenant pode marcar como falso positivo (ADR-117). */
  onMarkFalsePositive?: (alertId: string) => void;
}): JSX.Element | null {
  const [justification, setJustification] = useState('');

  if (!open || alert === null) return null;

  const cat = CATEGORY_LABELS[alert.category];
  const sev = SEVERITY_INFO[alert.severity];
  const blocking = SEVERITY_BLOCKS[alert.severity] ?? SEVERITY_BLOCKS.low!;

  const handleAccept = () => {
    if (!justification.trim()) return;
    onAcceptWithJustification?.(alert.alertId, justification);
    setJustification('');
    onClose();
  };

  const handleFalsePositive = () => {
    onMarkFalsePositive?.(alert.alertId);
    onClose();
  };

  const acceptConfirmProps =
    onAcceptWithJustification !== undefined
      ? { confirmLabel: 'Aceitar com justificativa', onConfirm: handleAccept }
      : {};

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title="ConflictAlert — detalhes"
      {...acceptConfirmProps}
      cancelLabel="Cancelar"
      maxWidth="640px"
    >
      <div className="space-y-4 text-sm">
        {/* Categoria + severity */}
        <div>
          <p className="text-2xs uppercase tracking-wide font-semibold text-text-secondary mb-1">
            Categoria + severity
          </p>
          <div className="flex items-center gap-2">
            <span className="font-semibold text-text-primary">{cat?.label ?? alert.category}</span>
            <span className="font-mono text-2xs text-text-secondary">
              · severity={alert.severity} ({sev?.label ?? alert.severity})
            </span>
          </div>
          {cat?.description !== undefined && (
            <p className="text-xs text-text-secondary mt-0.5">{cat.description}</p>
          )}
        </div>

        {/* Detection context */}
        <div>
          <p className="text-2xs uppercase tracking-wide font-semibold text-text-secondary mb-1">
            Detectado em
          </p>
          <p className="text-xs text-text-secondary">{alert.createdAt}</p>
          {alert.detectionContext !== null && (
            <p className="text-xs text-text-secondary mt-1 italic">{alert.detectionContext}</p>
          )}
        </div>

        {/* Parties envolvidas r7b §32.4 — exibido quando backend expõe */}
        {alert.parties.length > 0 && (
          <div>
            <p className="text-2xs uppercase tracking-wide font-semibold text-text-secondary mb-1">
              Parties envolvidas
            </p>
            <div className="flex flex-col gap-1.5">
              {alert.parties.map((party, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <span className="font-mono text-2xs text-text-secondary uppercase bg-surface-raised border border-border-subtle rounded px-1 py-0.5">
                    {party.type}
                  </span>
                  <span className="font-medium text-text-primary">{party.name}</span>
                  <span className="text-text-secondary">— {party.role}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Severity rationale da IA — quando disponível */}
        {alert.rationale !== null && (
          <div>
            <p className="text-2xs uppercase tracking-wide font-semibold text-text-secondary mb-1">
              Severity assessment (IA)
            </p>
            <p className="text-xs text-text-secondary italic">&ldquo;{alert.rationale}&rdquo;</p>
          </div>
        )}

        {/* §32.3 Bloqueios automáticos por severity */}
        <div>
          <p className="text-2xs uppercase tracking-wide font-semibold text-text-secondary mb-2">
            Bloqueios automáticos por severity (§32.3)
          </p>
          <div className="border border-border-subtle rounded overflow-hidden text-xs">
            <div className="grid grid-cols-4 bg-surface-raised text-text-secondary">
              <span className="px-2 py-1.5 font-medium border-r border-border-subtle">
                severity
              </span>
              <span className="px-2 py-1.5 font-medium border-r border-border-subtle text-center">
                H_c
              </span>
              <span className="px-2 py-1.5 font-medium border-r border-border-subtle text-center">
                MCP tools
              </span>
              <span className="px-2 py-1.5 font-medium text-center">Composer</span>
            </div>
            {SEVERITY_ORDER.map((s) => {
              const rowInfo = SEVERITY_BLOCKS[s];
              if (rowInfo === undefined) return null;
              const isCurrent = s === alert.severity;
              return (
                <div
                  key={s}
                  className="grid grid-cols-4 border-t border-border-subtle"
                  style={
                    isCurrent ? { background: 'var(--warning-bg)', fontWeight: 600 } : undefined
                  }
                >
                  <span
                    className="px-2 py-1.5 border-r border-border-subtle"
                    style={{ color: isCurrent ? 'var(--warning-fg)' : 'var(--fg-2)' }}
                  >
                    {SEVERITY_INFO[s]?.label ?? s}
                  </span>
                  <BlockCell value={rowInfo.blocksHC} />
                  <BlockCell value={rowInfo.blocksMCP} />
                  <BlockCell value={rowInfo.blocksComposer} />
                </div>
              );
            })}
          </div>
          {blocking.blocksComposer !== false && (
            <p className="text-2xs mt-1.5" style={{ color: 'var(--danger-fg)' }}>
              ⚠ Severity crítica: Composer em somente leitura — atendente não pode enviar mensagens.
            </p>
          )}
        </div>

        {/* Bonds envolvidos */}
        {alert.bondsInvolved.length > 0 && (
          <div>
            <p className="text-2xs uppercase tracking-wide font-semibold text-text-secondary mb-1">
              Bonds envolvidos
            </p>
            <div className="flex flex-wrap gap-1">
              {alert.bondsInvolved.map((bondId) => (
                <span
                  key={bondId}
                  className="font-mono text-2xs bg-surface-raised border border-border-subtle rounded px-1.5 py-0.5 text-text-secondary"
                >
                  {bondId.slice(0, 16)}…
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Justificativa para aceitar */}
        {onAcceptWithJustification !== undefined && (
          <div>
            <label className="block text-2xs uppercase tracking-wide font-semibold text-text-secondary mb-1">
              Justificativa para aceitar e prosseguir{' '}
              <span style={{ color: 'var(--danger-fg)' }}>*</span>
            </label>
            <textarea
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              placeholder="Descreva por que prosseguir é apropriado · audit forense reforçado em adversarial-strict."
              className="input w-full resize-none text-xs"
              rows={3}
            />
            <p className="text-2xs font-mono text-text-secondary opacity-60 mt-1">
              audit: conflict.alert.accepted · ADR-117
            </p>
          </div>
        )}

        {/* Falso positivo — ação gestor */}
        {onMarkFalsePositive !== undefined && (
          <div className="pt-1 border-t border-border-subtle">
            <button
              type="button"
              onClick={handleFalsePositive}
              className="text-xs text-text-secondary hover:text-text-primary underline"
            >
              Marcar como falso positivo (gestor / admin_tenant)
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}

function BlockCell({ value }: { value: boolean | 'partial' | 'read-only' }): JSX.Element {
  // Fase C Brief 2A r7b — DS canonical via tokens (Spec §1.5):
  //   true / read-only → --danger-fg (block)
  //   'partial'        → --warning-fg
  //   false            → --fg-3 + opacity (não-bloqueio)
  if (value === true) {
    return (
      <span
        className="px-2 py-1.5 border-r last:border-r-0 border-border-subtle text-center font-medium"
        style={{ color: 'var(--danger-fg)' }}
      >
        sim
      </span>
    );
  }
  if (value === 'partial') {
    return (
      <span
        className="px-2 py-1.5 border-r last:border-r-0 border-border-subtle text-center"
        style={{ color: 'var(--warning-fg)' }}
      >
        parcial
      </span>
    );
  }
  if (value === 'read-only') {
    return (
      <span
        className="px-2 py-1.5 border-r last:border-r-0 border-border-subtle text-center font-medium"
        style={{ color: 'var(--danger-fg)' }}
      >
        somente leitura
      </span>
    );
  }
  return (
    <span className="px-2 py-1.5 border-r last:border-r-0 border-border-subtle text-center text-text-secondary opacity-40">
      não
    </span>
  );
}

const SEVERITY_ORDER = ['low', 'medium', 'high', 'critical'] as const;

const SEVERITY_INFO: Record<string, { label: string }> = {
  low: { label: 'Baixa' },
  medium: { label: 'Média' },
  high: { label: 'Alta' },
  critical: { label: 'Crítica' },
};

const SEVERITY_BLOCKS: Record<
  string,
  { blocksHC: boolean; blocksMCP: boolean | 'partial'; blocksComposer: boolean | 'read-only' }
> = {
  low: { blocksHC: false, blocksMCP: false, blocksComposer: false },
  medium: { blocksHC: false, blocksMCP: false, blocksComposer: false },
  high: { blocksHC: true, blocksMCP: 'partial', blocksComposer: false },
  critical: { blocksHC: true, blocksMCP: true, blocksComposer: 'read-only' },
};

const CATEGORY_LABELS: Record<string, { label: string; description: string }> = {
  'cross-tenant-customer': {
    label: 'Cliente cross-tenant',
    description: 'Cliente atendido por outro tenant simultaneamente.',
  },
  'conflict-of-interest': {
    label: 'Conflito de interesse',
    description: 'Operador relacionado a parte adversária.',
  },
  'prior-case-same-party': {
    label: 'Caso anterior com mesma parte',
    description: 'Mesma parte teve caso anterior em outro contexto.',
  },
  'data-confidentiality': {
    label: 'Quebra potencial de sigilo',
    description: 'Informação confidencial pode vazar entre Bonds.',
  },
  'regulatory-conflict': {
    label: 'Conflito regulatório',
    description: 'Ação viola política regulatória do tenant ou compliance.',
  },
};
